import { useState, useEffect, useRef } from "react";

const GOLD = "#c9a84c";
const GOLD_L = "#dfc47a";
const TERRA = "#c17c5a";
const IVORY = "#f0ebe0";
const CREAM = "#d4cfc4";
const BG = "#0a0b0f";
const SF = "#101118";
const MU = "#6b6570";
const BD = "#1c1c28";
const RAD = "14px";
const RAD_SM = "10px";
const SHADOW = "0 4px 24px rgba(0,0,0,.35)";
const SHADOW_HOVER = "0 12px 48px rgba(0,0,0,.5)";

const useV = (t = 0.1) => {
  const r = useRef(null);
  const [v, sV] = useState(false);
  useEffect(() => {
    const e = r.current;
    if (!e) return;
    const o = new IntersectionObserver(([x]) => { if (x.isIntersecting) { sV(true); o.unobserve(e); } }, { threshold: t });
    o.observe(e);
    return () => o.disconnect();
  }, [t]);
  return [r, v];
};

const R = ({ children, delay = 0, y = 40, className = "" }) => {
  const [r, v] = useV();
  return (
    <div ref={r} className={className} style={{
      opacity: v ? 1 : 0, transform: v ? "none" : `translateY(${y}px)`,
      transition: `all 0.85s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
    }}>{children}</div>
  );
};

// Web3Forms: Replace with your access key from https://web3forms.com (free, 30 seconds)
const W3F_KEY = "8abda131-17ad-478b-b3a0-eb569e274417";

const useCases = [
  {
    audience: "Galleries",
    headline: "Know which collector to call — before you hang the show.",
    scenarios: [
      "Match artworks to likely buyers based on taste, budget, and purchase history",
      "Price primary market works with intelligence from 500K+ auction records",
      "Automate personalized outreach that reads like you wrote it yourself",
      "Track which collectors engage with your viewing rooms and follow up at the right moment",
    ],
    icon: "◧",
  },
  {
    audience: "Artists",
    headline: "See your market clearly. Grow it strategically.",
    scenarios: [
      "Track your auction results, exhibition history, and market positioning in one place",
      "Identify which galleries and collectors are buying artists at your career stage",
      "Get pricing intelligence so you and your gallery price with confidence, not guesswork",
      "Understand where your work sits in the market — and what moves the needle",
    ],
    icon: "✦",
  },
  {
    audience: "Collectors & Advisors",
    headline: "Stop flying blind on your next acquisition.",
    scenarios: [
      "See an artist's full auction history, trends, and comparable sales in seconds",
      "Get AI-driven alerts when works by artists on your watchlist come to market",
      "Run due diligence — provenance, authenticity, legal status — before you commit",
      "Track your collection's value over time with real market data, not guesswork",
    ],
    icon: "◆",
  },
  {
    audience: "Family Offices",
    headline: "Manage art the way you manage every other asset.",
    scenarios: [
      "Portfolio-grade reporting your wealth manager will actually understand",
      "AI-powered valuations tied to live market data, not outdated appraisals",
      "Automated AML screening and sanctions checking on every transaction",
      "Estate planning intelligence — tax implications, donation strategies, succession",
    ],
    icon: "◈",
  },
  {
    audience: "Art Fairs",
    headline: "Every booth, every buyer, every opportunity — connected.",
    scenarios: [
      "Predict which VIP collectors are most likely to attend and buy",
      "Match exhibitor inventory to pre-registered buyer preferences",
      "Real-time fair analytics — traffic, engagement, sales velocity by booth",
      "Post-fair intelligence reports that prove ROI to exhibitors and sponsors",
    ],
    icon: "◫",
  },
  {
    audience: "Institutions",
    headline: "Weeks of provenance research. Done in an afternoon.",
    scenarios: [
      "AI-assisted provenance research across global auction and exhibition databases",
      "Automated condition report generation and tracking across your collection",
      "Acquisition intelligence — compare candidates on market trajectory and exhibition history",
      "Collection analytics that help boards make data-informed decisions",
    ],
    icon: "▣",
  },
];

const products = [
  { name: "ArtCollectorIQ", url: "artcollectoriq.com", desc: "Auction analytics, artist market reports, and AI-driven intelligence for smarter buying and selling decisions.", tag: "MARKET INTELLIGENCE" },
  { name: "ArtGalleryIQ", url: "artgalleryiq.com", desc: "AI-powered tools that help galleries match artwork to collectors and close more sales.", tag: "GALLERY SALES" },
  { name: "ArtCheck", url: "artcheck.work", desc: "Due diligence in one place — authentication, provenance tracing, AML screening, and risk reports.", tag: "DUE DILIGENCE" },
];

export default function ArtAgentV6() {
  const [scrollY, setScrollY] = useState(0);
  const [openCase, setOpenCase] = useState(0);
  const [formData, setFormData] = useState({ name: "", email: "", org: "", role: "", message: "" });
  const [formStatus, setFormStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const navO = Math.min(scrollY / 200, 0.94);

  return (
    <div style={{ background: BG, color: IVORY, fontFamily: "'Outfit', sans-serif", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
        *{margin:0;padding:0;box-sizing:border-box}
        html{scroll-behavior:smooth}
        ::selection{background:${GOLD};color:${BG}}

        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}

        .grain::after{
          content:'';position:fixed;top:-50%;left:-50%;width:200%;height:200%;pointer-events:none;z-index:9999;
          background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.02'/%3E%3C/svg%3E");
        }

        .mono{font-family:'JetBrains Mono',monospace}

        .nav-link{color:${CREAM}77;text-decoration:none;font-size:13px;font-weight:500;letter-spacing:1px;transition:color .25s;cursor:pointer}
        .nav-link:hover{color:${GOLD}}

        .cta-primary{
          background:${GOLD};color:${BG};border:none;padding:22px 56px;
          font-family:'Outfit',sans-serif;font-weight:800;font-size:16px;
          letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;
          position:relative;overflow:hidden;transition:all .35s;
          display:inline-block;text-decoration:none;border-radius:${RAD_SM};
        }
        .cta-primary:hover{background:${GOLD_L};transform:translateY(-2px);box-shadow:0 10px 40px ${GOLD}33}
        .cta-primary::after{
          content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.15),transparent);
          transition:left .6s;
        }
        .cta-primary:hover::after{left:100%}

        .cta-ghost{
          background:transparent;color:${GOLD};border:1.5px solid ${GOLD}44;
          padding:20px 48px;font-family:'Outfit',sans-serif;font-weight:700;
          font-size:15px;letter-spacing:1.5px;text-transform:uppercase;
          cursor:pointer;transition:all .3s;text-decoration:none;display:inline-block;
          border-radius:${RAD_SM};
        }
        .cta-ghost:hover{border-color:${GOLD};background:${GOLD}0a}

        .sl{font-family:'Outfit',sans-serif;font-size:13px;font-weight:600;color:${GOLD};letter-spacing:5px;text-transform:uppercase;margin-bottom:20px;display:flex;align-items:center;gap:14px}
        .sl::before{content:'';width:32px;height:1.5px;background:${GOLD}66}

        .marquee-wrap{overflow:hidden;border-top:1px solid ${BD};border-bottom:1px solid ${BD};padding:18px 0}
        .marquee-track{display:flex;animation:marquee 30s linear infinite;width:max-content}

        .use-tab{
          padding:18px 28px;cursor:pointer;transition:all .3s;
          border:none;border-left:2px solid transparent;font-weight:600;font-size:18px;
          display:flex;align-items:center;gap:12px;color:${MU};
          border-radius:0 ${RAD_SM} ${RAD_SM} 0;
        }
        .use-tab:hover{color:${CREAM};background:${GOLD}04}
        .use-tab.active{color:${GOLD};border-left-color:${GOLD};background:${GOLD}08}

        .scenario-item{
          padding:18px 24px;border-left:1.5px solid ${BD};
          transition:all .3s;font-size:18px;color:${CREAM}aa;line-height:1.65;
          border-radius:0 ${RAD_SM} ${RAD_SM} 0;
        }
        .scenario-item:hover{border-left-color:${GOLD};color:${IVORY};padding-left:32px;background:${GOLD}04}

        .product-card{
          border:1px solid ${BD};padding:48px 40px;background:${SF};
          transition:all .45s;cursor:pointer;position:relative;overflow:hidden;
          border-radius:${RAD};box-shadow:${SHADOW};
          display:flex;flex-direction:column;height:100%;
        }
        .product-card:hover{border-color:${GOLD}44;transform:translateY(-4px);box-shadow:${SHADOW_HOVER}}

        .trust-item{
          padding:28px 32px;background:${SF};border:1px solid ${BD};
          border-left:2px solid ${GOLD}44;transition:all .3s;
          border-radius:${RAD_SM};box-shadow:${SHADOW};
        }
        .trust-item:hover{border-left-color:${GOLD};transform:translateX(4px)}

        .tier-card{
          border:1px solid ${BD};padding:52px 40px;background:${BG};
          display:flex;flex-direction:column;height:100%;transition:all .3s;
          border-radius:${RAD};box-shadow:${SHADOW};
        }
        .tier-card:hover{border-color:${GOLD}33;transform:translateY(-3px);box-shadow:${SHADOW_HOVER}}

        .form-input{
          width:100%;padding:16px 20px;background:${SF};border:1px solid ${BD};
          border-radius:${RAD_SM};color:${IVORY};font-family:'Outfit',sans-serif;
          font-size:16px;font-weight:400;outline:none;transition:all .3s;
        }
        .form-input:focus{border-color:${GOLD}88;box-shadow:0 0 0 3px ${GOLD}11}
        .form-input::placeholder{color:${MU}}
        .form-select{
          width:100%;padding:16px 20px;background:${SF};border:1px solid ${BD};
          border-radius:${RAD_SM};color:${IVORY};font-family:'Outfit',sans-serif;
          font-size:16px;font-weight:400;outline:none;transition:all .3s;
          appearance:none;cursor:pointer;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236b6570' fill='none' stroke-width='1.5'/%3E%3C/svg%3E");
          background-repeat:no-repeat;background-position:right 20px center;
        }
        .form-select:focus{border-color:${GOLD}88;box-shadow:0 0 0 3px ${GOLD}11}
        .form-select option{background:${SF};color:${IVORY};padding:8px}
        .form-textarea{
          width:100%;padding:16px 20px;background:${SF};border:1px solid ${BD};
          border-radius:${RAD_SM};color:${IVORY};font-family:'Outfit',sans-serif;
          font-size:16px;font-weight:400;outline:none;transition:all .3s;
          resize:vertical;min-height:100px;
        }
        .form-textarea:focus{border-color:${GOLD}88;box-shadow:0 0 0 3px ${GOLD}11}
        .form-textarea::placeholder{color:${MU}}

        .cred-badge{
          border-radius:${RAD};box-shadow:${SHADOW};
        }

        .stat-box{
          background:${SF};border:1px solid ${BD};border-radius:${RAD_SM};
          padding:20px 28px;box-shadow:${SHADOW};
        }

        .use-panel-wrap{
          border:1px solid ${BD};border-radius:${RAD};overflow:hidden;
          box-shadow:${SHADOW};background:${SF};
        }

        @media(max-width:900px){
          .hero-h{font-size:40px!important}
          .g2{grid-template-columns:1fr!important}
          .g3{grid-template-columns:1fr!important}
          .hero-ctas{flex-direction:column!important}
          .nav-links{display:none!important}
          .use-panel{flex-direction:column!important}
          .use-tabs{flex-direction:row!important;overflow-x:auto!important}
          .cred-row{flex-direction:column!important;gap:16px!important}
          .sia-watermark{font-size:40px!important}
          .sia-inner{flex-direction:column!important;text-align:center!important}
          .form-grid{grid-template-columns:1fr!important}
        }
      `}</style>

      <div className="grain" />

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, padding: "18px 40px",
        background: `rgba(10,11,15,${navO})`,
        backdropFilter: navO > .1 ? "blur(20px)" : "none",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: navO > .1 ? `1px solid ${BD}` : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 7, height: 7, background: GOLD, borderRadius: "50%", animation: "pulse 3s ease infinite" }} />
          <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: 2.5, textTransform: "uppercase", color: IVORY }}>
            Art<span style={{ color: GOLD }}>Agent</span><span style={{ color: MU, fontWeight: 400 }}>.art</span>
          </span>
        </div>
        <div className="nav-links" style={{ display: "flex", gap: 28 }}>
          <a href="#how" className="nav-link">What We Do</a>
          <a href="#products" className="nav-link">Products</a>
          <a href="#approach" className="nav-link">Approach</a>
          <a href="#start" className="nav-link">Get Started</a>
          <a href="#contact" className="nav-link" style={{ color: GOLD }}>Book a Call</a>
        </div>
      </nav>

      {/* ═══════════════ HERO ═══════════════ */}
      <section style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "140px 48px 100px", position: "relative",
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "hidden", opacity: 0.025,
        }}>
          {[...Array(20)].map((_, i) => (
            <div key={i} style={{
              position: "absolute", top: -100, left: `${i * 8}%`,
              width: 1, height: "150%", background: GOLD,
              transform: "rotate(25deg)", transformOrigin: "top left",
            }} />
          ))}
        </div>

        <div style={{
          position: "absolute", top: 120, right: 70,
          width: 130, height: 130, border: `1px solid ${GOLD}12`,
          borderRadius: RAD, transform: `rotate(${scrollY * 0.015}deg)`,
        }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: 1000 }}>
          <R delay={0.05}>
            <div className="sl" style={{ marginBottom: 36, fontSize: 14 }}>AI for the Art World</div>
          </R>

          <R delay={0.12}>
            <h1 className="hero-h" style={{
              fontSize: "clamp(48px, 8vw, 100px)", fontWeight: 900,
              lineHeight: 1.15, letterSpacing: -2.5, marginBottom: 40,
            }}>
              Smarter Sales.
              <br />
              <span style={{ color: GOLD }}>Sharper Acquisitions.</span>
              <br />
              <span style={{ color: TERRA }}>Clearer Intelligence.</span>
            </h1>
          </R>

          <R delay={0.22}>
            <p style={{
              fontSize: 20, lineHeight: 1.75, color: CREAM, maxWidth: 560, marginBottom: 48, fontWeight: 400,
            }}>
              We help galleries sell more, artists grow strategically, collectors buy smarter,
              and institutions work faster — using AI built specifically
              for how the art market actually operates.
              <span style={{ fontWeight: 700, color: IVORY }}> Tools that understand your world.</span>
            </p>
          </R>

          <R delay={0.32}>
            <div className="hero-ctas" style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <a href="#contact" className="cta-primary">Book a Strategy Call</a>
              <a href="#how" className="cta-ghost">See How It Works →</a>
            </div>
          </R>

          <R delay={0.45}>
            <div className="cred-row" style={{
              marginTop: 72, display: "flex", gap: 20, alignItems: "stretch", flexWrap: "wrap",
            }}>
              {[
                { num: "3", label: "AI PRODUCTS LIVE" },
                { num: "500K+", label: "AUCTION RECORDS" },
                { num: "6", label: "CLIENT VERTICALS" },
              ].map((s, i) => (
                <div key={i} className="stat-box" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{ fontSize: 40, fontWeight: 800, color: GOLD, lineHeight: 1 }}>{s.num}</span>
                  <span className="mono" style={{ fontSize: 10, color: MU, letterSpacing: 2 }}>{s.label}</span>
                </div>
              ))}
            </div>
          </R>
        </div>
      </section>

      {/* ═══════════════ SOTHEBY'S WATERMARK SECTION ═══════════════ */}
      <section style={{
        padding: "80px 48px", borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}`,
        position: "relative", overflow: "hidden", background: SF,
      }}>
        <div className="sia-watermark" style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "clamp(60px, 10vw, 130px)",
          fontWeight: 900, letterSpacing: 8,
          color: GOLD, opacity: 0.06,
          whiteSpace: "nowrap", textTransform: "uppercase",
          userSelect: "none", pointerEvents: "none",
        }}>
          SOTHEBY'S INSTITUTE OF ART
        </div>

        <div style={{
          position: "relative", zIndex: 2, maxWidth: 1100, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <R>
            <div className="cred-badge sia-inner" style={{
              display: "flex", alignItems: "center", gap: 40, flexWrap: "wrap",
              padding: "36px 52px", border: `1px solid ${GOLD}18`, background: `${GOLD}04`,
            }}>
              <div style={{
                width: 80, height: 80, border: `1.5px solid ${GOLD}33`,
                borderRadius: RAD_SM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <span style={{ fontSize: 30, fontWeight: 800, color: GOLD, letterSpacing: 2 }}>SIA</span>
              </div>

              <div>
                <div style={{
                  fontSize: 24, fontWeight: 700, letterSpacing: 4,
                  textTransform: "uppercase", color: IVORY, marginBottom: 8,
                }}>
                  Sotheby's Institute of Art
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ height: 1, width: 40, background: `${GOLD}44` }} />
                  <span style={{ fontSize: 15, fontWeight: 600, color: GOLD, letterSpacing: 4, textTransform: "uppercase" }}>Alumni</span>
                  <div style={{ height: 1, flex: 1, maxWidth: 100, background: `${GOLD}44` }} />
                </div>
              </div>

              <div style={{ borderLeft: `1px solid ${BD}`, paddingLeft: 36, maxWidth: 360 }}>
                <p style={{ fontSize: 16, color: CREAM, fontWeight: 400, lineHeight: 1.7 }}>
                  Art market expertise earned at the highest level.
                  We understand provenance, connoisseurship, and market dynamics from the inside.
                </p>
              </div>
            </div>
          </R>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...Array(3)].map((_, i) => (
            <span key={i} className="mono" style={{ fontSize: 12, letterSpacing: 4, color: MU, whiteSpace: "nowrap", paddingRight: 56 }}>
              SELL MORE ART &nbsp;◆&nbsp; GROW YOUR MARKET &nbsp;◆&nbsp; FIND THE RIGHT BUYERS &nbsp;◆&nbsp; TRACK YOUR COLLECTION &nbsp;◆&nbsp; VERIFY BEFORE YOU BUY &nbsp;◆&nbsp; PRICE WITH DATA &nbsp;◆&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════ USE CASE EXPLORER ═══════════════ */}
      <section id="how" style={{ padding: "140px 48px", borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <R>
            <div className="sl">What We Do</div>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 800, lineHeight: 1.08, marginBottom: 20, letterSpacing: -1 }}>
              Pick your world.
              <br />
              <span style={{ color: MU }}>We'll show you what changes.</span>
            </h2>
            <p style={{ fontSize: 18, color: CREAM, maxWidth: 540, lineHeight: 1.8, marginBottom: 72, fontWeight: 400 }}>
              AI isn't a feature to bolt on — it's a new way of working. Here's what it looks like in practice, for the people who do this work every day.
            </p>
          </R>

          <div className="use-panel-wrap">
            <div className="use-panel" style={{ display: "flex", gap: 0, minHeight: 440 }}>
              <div className="use-tabs" style={{
                display: "flex", flexDirection: "column", flexShrink: 0,
                borderRight: `1px solid ${BD}`, background: `${BG}88`,
              }}>
                {useCases.map((uc, i) => (
                  <div key={i} className={`use-tab ${openCase === i ? "active" : ""}`} onClick={() => setOpenCase(i)}>
                    <span style={{ fontSize: 20, opacity: openCase === i ? 1 : .35 }}>{uc.icon}</span>
                    {uc.audience}
                  </div>
                ))}
              </div>

              <div style={{ flex: 1, padding: "36px 52px" }}>
                <R key={openCase} y={15}>
                  <div className="mono" style={{ fontSize: 11, letterSpacing: 4, fontWeight: 600, color: GOLD, marginBottom: 24 }}>
                    FOR {useCases[openCase].audience.toUpperCase()}
                  </div>
                  <h3 style={{
                    fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700,
                    lineHeight: 1.2, marginBottom: 36, maxWidth: 500, color: IVORY, letterSpacing: -0.5,
                  }}>
                    {useCases[openCase].headline}
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {useCases[openCase].scenarios.map((s, j) => (
                      <div key={j} className="scenario-item">{s}</div>
                    ))}
                  </div>
                </R>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MANIFESTO ═══════════════ */}
      <section style={{ padding: "130px 48px", background: SF }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <R>
            <p style={{
              fontSize: "clamp(24px, 3.2vw, 38px)", lineHeight: 1.55, fontWeight: 400, color: CREAM,
            }}>
              The gallery world still prices by instinct. Artists don't see their own market clearly.
              Collectors rely on whisper networks. Family offices track art portfolios
              in spreadsheets.{" "}
              <span style={{ color: IVORY, fontWeight: 700 }}>
                Not because better tools don't exist — because nobody built them for this world specifically.
              </span>{" "}
              We did. Three times. With the fluency of an art insider and the rigour of an engineering team.{" "}
              <span style={{ color: GOLD, fontWeight: 700 }}>And we're just getting started.</span>
            </p>
          </R>
        </div>
      </section>

      {/* ═══════════════ PRODUCTS ═══════════════ */}
      <section id="products" style={{ padding: "140px 48px", borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <R>
            <div className="sl">Our Products</div>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 800, lineHeight: 1.08, marginBottom: 20, letterSpacing: -1 }}>
              Built for this industry.
              <br />
              <span style={{ color: MU }}>Live and working today.</span>
            </h2>
            <p style={{ fontSize: 18, color: CREAM, maxWidth: 540, lineHeight: 1.8, marginBottom: 72, fontWeight: 400 }}>
              We don't just advise — we build. These are production AI platforms already serving the art market.
            </p>
          </R>

          <div className="g3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }}>
            {products.map((p, i) => (
              <R key={i} delay={i * .1}>
                <div
                  className="product-card"
                  onClick={() => window.open(`https://www.${p.url}`, '_blank')}
                >
                  <div className="mono" style={{
                    fontSize: 10, letterSpacing: 3, fontWeight: 600,
                    color: BG, background: [GOLD, TERRA, GOLD_L][i],
                    padding: "5px 16px", display: "inline-block", marginBottom: 28,
                    borderRadius: "6px",
                  }}>
                    {p.tag}
                  </div>
                  <h3 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16, color: IVORY }}>{p.name}</h3>
                  <p style={{ fontSize: 16, color: CREAM, opacity: .75, lineHeight: 1.7, marginBottom: 24, fontWeight: 400, flex: 1 }}>{p.desc}</p>
                  <div className="mono" style={{ fontSize: 13, color: MU, marginTop: "auto" }}>{p.url} →</div>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ APPROACH ═══════════════ */}
      <section id="approach" style={{ padding: "140px 48px", borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <R>
            <div className="sl">Our Approach</div>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 800, lineHeight: 1.08, marginBottom: 24, letterSpacing: -1 }}>
              Art market fluency.
              <br />
              <span style={{ color: MU }}>Engineering discipline.</span>
            </h2>
          </R>

          <div className="g2" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, marginTop: 60, alignItems: "start",
          }}>
            <R delay={.1}>
              <div>
                <p style={{ fontSize: 19, lineHeight: 1.85, color: CREAM, marginBottom: 28, fontWeight: 400 }}>
                  We studied at Sotheby's Institute of Art. We've sat in auction rooms and walked fair booths. We understand provenance, connoisseurship, and why a gallery director's Tuesday looks nothing like a tech founder's.
                </p>
                <p style={{ fontSize: 19, lineHeight: 1.85, color: IVORY, fontWeight: 600, marginBottom: 28 }}>
                  That context is why our AI actually works — it's built around your workflows, not shoe-horned in from Silicon Valley.
                </p>
                <p style={{ fontSize: 17, lineHeight: 1.85, color: MU, fontWeight: 400 }}>
                  And because we don't run a marketplace, auction house, or take commissions on sales — the only thing we're optimizing for is your success.
                </p>
              </div>
            </R>

            <R delay={.2}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { t: "We speak art, not just AI", d: "Sotheby's Institute trained. We know the difference between a gallery's primary market challenges and an auction house's consignment pipeline." },
                  { t: "We've built what we recommend", d: "Three live platforms — not concepts, not pitch decks. Real products serving real users in the art market right now." },
                  { t: "No conflicts, no commissions", d: "We don't sell art, broker deals, or take transaction fees. Our only revenue comes from making you more effective." },
                  { t: "Strategy through implementation", d: "We don't hand you a 60-page PDF and disappear. We build the tools, train your team, and stay until it works." },
                ].map((item, i) => (
                  <div key={i} className="trust-item">
                    <h4 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: IVORY }}>{item.t}</h4>
                    <p style={{ fontSize: 14, color: MU, lineHeight: 1.65 }}>{item.d}</p>
                  </div>
                ))}
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ═══════════════ GET STARTED ═══════════════ */}
      <section id="start" style={{ padding: "140px 48px", background: SF, borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <R>
            <div className="sl">Get Started</div>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 800, lineHeight: 1.08, marginBottom: 72, letterSpacing: -1 }}>
              Three ways to work with us.
            </h2>
          </R>

          <div className="g3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }}>
            {[
              {
                step: "01", tier: "STRATEGY SESSION", price: "One-Time",
                desc: "90 minutes focused on your business. You leave with a clear picture of where AI fits, which tools to use, and what to do first. No jargon. No upsell.",
                ideal: "Anyone exploring AI for their art business", color: GOLD,
              },
              {
                step: "02", tier: "BUILD & IMPLEMENT", price: "Project-Based",
                desc: "We design and deploy AI solutions tailored to your workflow — collector matching, pricing intelligence, due diligence pipelines, or custom analytics.",
                ideal: "Galleries, fairs, artists, institutions with specific needs", color: TERRA,
              },
              {
                step: "03", tier: "ONGOING PARTNER", price: "Monthly Retainer",
                desc: "An embedded AI partner for your art business. Monthly sessions, priority tool access, custom reporting, and continuous optimization as the market evolves.",
                ideal: "Family offices, major collections, enterprise galleries", color: GOLD_L,
              },
            ].map((s, i) => (
              <R key={i} delay={i * .1}>
                <div className="tier-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
                    <div className="mono" style={{
                      fontSize: 10, letterSpacing: 3, fontWeight: 600,
                      color: BG, background: s.color, padding: "5px 16px", borderRadius: "6px",
                    }}>
                      {s.tier}
                    </div>
                    <span style={{ fontSize: 40, fontWeight: 900, color: s.color, lineHeight: 1, opacity: .3 }}>{s.step}</span>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 18, color: s.color }}>{s.price}</div>
                  <p style={{ fontSize: 16, color: CREAM, opacity: .8, lineHeight: 1.7, marginBottom: 28, flex: 1, fontWeight: 400 }}>{s.desc}</p>
                  <div style={{
                    fontSize: 13, color: MU, fontWeight: 500,
                    padding: "16px 0 0", borderTop: `1px solid ${BD}`,
                  }}>
                    Best for: {s.ideal}
                  </div>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CONTACT FORM ═══════════════ */}
      <section id="contact" style={{
        padding: "140px 48px", textAlign: "center", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: 600, height: 600,
          background: `radial-gradient(circle, ${GOLD}08 0%, transparent 55%)`,
          borderRadius: "50%",
        }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: 620, margin: "0 auto" }}>
          <R>
            <div className="sl" style={{ justifyContent: "center" }}>Let's Talk</div>
            <h2 style={{
              fontSize: "clamp(40px, 6vw, 76px)", fontWeight: 900,
              lineHeight: 1.05, marginBottom: 16, letterSpacing: -2,
            }}>
              Your art business,
              <br />
              <span style={{ color: GOLD }}>working smarter.</span>
            </h2>
            <p style={{
              fontSize: 18, color: CREAM, maxWidth: 460, margin: "0 auto 52px",
              lineHeight: 1.85, fontWeight: 400,
            }}>
              Tell us about your business. We'll follow up within 24 hours with a time for your free 30-minute strategy call.
            </p>
          </R>

          {formStatus === "sent" ? (
            <R>
              <div style={{
                padding: "60px 40px", background: SF, border: `1px solid ${GOLD}33`,
                borderRadius: RAD, boxShadow: SHADOW, textAlign: "center",
              }}>
                <div style={{ fontSize: 48, marginBottom: 20 }}>✓</div>
                <h3 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12, color: GOLD }}>Message Sent</h3>
                <p style={{ fontSize: 17, color: CREAM, lineHeight: 1.7 }}>
                  Thank you for reaching out. We'll get back to you within 24 hours
                  to schedule your strategy call.
                </p>
              </div>
            </R>
          ) : (
            <R delay={.1}>
              <div style={{
                padding: "48px 44px", background: SF, border: `1px solid ${BD}`,
                borderRadius: RAD, boxShadow: SHADOW, textAlign: "left",
              }}>
                <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: CREAM, display: "block", marginBottom: 8 }}>Name *</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: CREAM, display: "block", marginBottom: 8 }}>Email *</label>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: CREAM, display: "block", marginBottom: 8 }}>Organization</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Gallery, collection, office..."
                      value={formData.org}
                      onChange={(e) => setFormData({ ...formData, org: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: CREAM, display: "block", marginBottom: 8 }}>I am a... *</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="" disabled>Select your role</option>
                      <option value="Gallery">Gallery</option>
                      <option value="Artist">Artist</option>
                      <option value="Collector">Collector</option>
                      <option value="Advisor">Art Advisor</option>
                      <option value="Family Office">Family Office</option>
                      <option value="Art Fair">Art Fair</option>
                      <option value="Institution">Institution</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 28 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: CREAM, display: "block", marginBottom: 8 }}>
                    What are you looking to solve?
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="E.g. I run a gallery and want to better match artworks to collectors..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <button
                  className="cta-primary"
                  style={{
                    width: "100%", fontSize: 16, padding: "20px",
                    opacity: formStatus === "sending" ? 0.6 : 1,
                    pointerEvents: formStatus === "sending" ? "none" : "auto",
                  }}
                  onClick={async () => {
                    if (!formData.name || !formData.email || !formData.role) {
                      setFormStatus("error");
                      setTimeout(() => setFormStatus("idle"), 3000);
                      return;
                    }
                    setFormStatus("sending");
                    try {
                      const res = await fetch("https://api.web3forms.com/submit", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          access_key: W3F_KEY,
                          subject: `ArtAgent.art — New Lead: ${formData.role} — ${formData.name}`,
                          from_name: "ArtAgent.art",
                          name: formData.name,
                          email: formData.email,
                          organization: formData.org || "Not specified",
                          role: formData.role,
                          message: formData.message || "No additional details provided.",
                        }),
                      });
                      const data = await res.json();
                      if (data.success) {
                        setFormStatus("sent");
                      } else {
                        setFormStatus("error");
                        setTimeout(() => setFormStatus("idle"), 3000);
                      }
                    } catch {
                      setFormStatus("error");
                      setTimeout(() => setFormStatus("idle"), 3000);
                    }
                  }}
                >
                  {formStatus === "sending" ? "Sending..." : formStatus === "error" ? "Please fill required fields" : "Request Your Strategy Call →"}
                </button>

                <p className="mono" style={{
                  fontSize: 11, color: MU, marginTop: 20, textAlign: "center", letterSpacing: 1,
                }}>
                  We'll respond within 24 hours · No spam, ever
                </p>
              </div>
            </R>
          )}

          <R delay={.3}>
            <p className="mono" style={{ fontSize: 12, color: MU, marginTop: 36, letterSpacing: 2 }}>
              CURRENTLY ACCEPTING NEW CLIENTS · Q2 2026
            </p>
          </R>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: "48px 48px", borderTop: `1px solid ${BD}` }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 20,
        }}>
          <div>
            <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: 2, color: IVORY }}>
              ART<span style={{ color: GOLD }}>AGENT</span><span style={{ color: MU }}>.ART</span>
            </span>
            <p style={{ fontSize: 12, color: MU, marginTop: 4 }}>AI for the Art World</p>
          </div>
          <div style={{ fontSize: 12, color: MU }}>© 2026 ArtAgent.art</div>
        </div>
      </footer>
    </div>
  );
}
